import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Share2, Clock, CheckCircle, XCircle, Loader2, Copy } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useJob, useCancelJob } from '../hooks/useVideo';

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: job, isLoading } = useJob(jobId!);
  const cancelMutation = useCancelJob();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Job not found</h2>
          <Link to="/gallery" className="btn-primary">
            Back to Gallery
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = {
    pending: { icon: Clock, color: 'text-yellow-400', label: 'Waiting in queue...' },
    processing: { icon: Loader2, color: 'text-blue-400', label: 'Generating your video...' },
    completed: { icon: CheckCircle, color: 'text-green-400', label: 'Video ready!' },
    failed: { icon: XCircle, color: 'text-red-400', label: 'Generation failed' },
    cancelled: { icon: XCircle, color: 'text-gray-400', label: 'Cancelled' },
  };

  const config = statusConfig[job.status];
  const Icon = config.icon;

  const handleDownload = () => {
    if (job.videoUrl) {
      const a = document.createElement('a');
      a.href = job.videoUrl;
      a.download = `video-${job.id}.mp4`;
      a.click();
    }
  };

  const handleShare = async () => {
    if (job.videoUrl) {
      await navigator.clipboard.writeText(job.videoUrl);
      toast.success('Video URL copied to clipboard');
    }
  };

  const handleCopyPrompt = async () => {
    await navigator.clipboard.writeText(job.prompt.text);
    toast.success('Prompt copied to clipboard');
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <Link
          to="/gallery"
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Gallery</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Video Preview */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="card p-0 overflow-hidden">
              <div className="aspect-video bg-gray-800 relative">
                {job.status === 'completed' && job.videoUrl ? (
                  <video
                    src={job.videoUrl}
                    controls
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-8">
                    <Icon
                      className={clsx(
                        'w-16 h-16 mb-4',
                        config.color,
                        job.status === 'processing' && 'animate-spin'
                      )}
                    />
                    <p className="text-gray-300 text-center">{config.label}</p>
                    {job.status === 'processing' && (
                      <div className="w-full max-w-xs mt-6">
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-primary-500 to-accent-500"
                            initial={{ width: 0 }}
                            animate={{ width: `${job.progress}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                        <p className="text-center text-sm text-gray-400 mt-2">
                          {job.progress}% complete
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              {job.status === 'completed' && (
                <div className="p-4 border-t border-gray-800 flex items-center space-x-4">
                  <button
                    onClick={handleDownload}
                    className="btn-primary flex-1 flex items-center justify-center space-x-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="btn-secondary flex items-center justify-center space-x-2 px-4"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              )}

              {(job.status === 'pending' || job.status === 'processing') && (
                <div className="p-4 border-t border-gray-800">
                  <button
                    onClick={() => cancelMutation.mutate(job.id)}
                    disabled={cancelMutation.isPending}
                    className="btn-secondary w-full"
                  >
                    Cancel Generation
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Prompt */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Prompt</h3>
                <button
                  onClick={handleCopyPrompt}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="text-gray-300">{job.prompt.text}</p>
              {job.prompt.negativePrompt && (
                <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-500 mb-1">Negative prompt:</p>
                  <p className="text-gray-400 text-sm">{job.prompt.negativePrompt}</p>
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Settings</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Provider</p>
                  <p className="text-white capitalize">{job.provider}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="text-white">{job.prompt.duration}s</p>
                </div>
                <div>
                  <p className="text-gray-500">Aspect Ratio</p>
                  <p className="text-white">{job.prompt.aspectRatio}</p>
                </div>
                <div>
                  <p className="text-gray-500">Resolution</p>
                  <p className="text-white">{job.prompt.resolution}</p>
                </div>
                <div>
                  <p className="text-gray-500">Frame Rate</p>
                  <p className="text-white">{job.prompt.fps} fps</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className={clsx('capitalize', config.color)}>{job.status}</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="card">
              <h3 className="text-lg font-semibold text-white mb-4">Timeline</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-300">
                    {new Date(job.createdAt).toLocaleString()}
                  </span>
                </div>
                {job.completedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Completed</span>
                    <span className="text-gray-300">
                      {new Date(job.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {job.error && (
              <div className="card border-red-500/50 bg-red-500/10">
                <h3 className="text-lg font-semibold text-red-400 mb-2">Error</h3>
                <p className="text-gray-300">{job.error}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
